import React, { Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import Banner from '@/components/UI/Banner';
import Header from '@/components/UI/Header';
import SubHeader from '@/components/UI/SubHeader';
import { successToast } from '@/helpers';
import { _isEmpty, _notNil } from '@/littledash';
import type { State } from '@/model/State.model';
import { ResponseType } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { web as webRoute } from '@/support/route';
import { RouteService } from '@/support/RouteService';
import { modalAction } from '@/utils/modal.tsx';

import { generateRepeatingIds, generateSequentialIds, REPEATING, SEQUENTIAL } from './Recaging.utils';
import type { ProposedCage, RecagingAnimal } from './Recaging.utils';
import { CageGenerationForm, IdGenerationForm } from './RecagingForm';
import { EmptyState, LoadingState, ProposedCageCard, SubmitSection } from './RecagingUI';
import { useCageGeneration, useRecagingData, useRecagingForm } from './hooks';

const Recaging: React.FC = () => {
  const { id: studyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openModal, closeModal } = modalAction(dispatch);

  const selectedSubjects = useSelector((state: State) => state.subjects.selectedSubjects);

  // Use custom hooks for data fetching and state management
  const { nextCage, studyCages, loading, studyCagesLoading, apiError } = useRecagingData(studyId!);

  const {
    formMethods,
    idGenerationMethod,
    setIdGenerationMethod,
    cageNamingMethod,
    setCageNamingMethod,
    deleteCages,
    setDeleteCages,
    tags,
    setTags,
    proposedCages,
    setProposedCages,
    existingGroupCageNames,
    setExistingGroupCageNames,
  } = useRecagingForm();

  const { recage } = useCageGeneration();

  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
  } = formMethods;

  const onRecageSubmit = (deleteCages: boolean): void => {
    if (deleteCages) {
      openModal('CONFIRM_DELETE_EMPTY_COLLECTIONS', {
        onClick: submitRecaging,
        closeModal,
      });
    } else {
      submitRecaging();
    }
  };

  const onChangeCageName = (newCageName: string, cageIndex: number) => {
    const update = [...proposedCages];
    update[cageIndex].cage = newCageName;
    setProposedCages(update);
  };

  const onChangeAnimalId = (newId: string, cageIndex: number, animalIndex: number) => {
    const update = [...proposedCages];
    update[cageIndex].animals[animalIndex].newAnimalId = newId;
    setProposedCages(update);
  };

  const { invoke: invokeRecage } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/recaging',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: true, slug: 'recaging-submit' } },
  });

  const submitRecaging = async () => {
    const studyApiId = selectedSubjects[0]?.study_api_id;
    if (!_notNil(studyApiId)) {
      return;
    }

    const response = await invokeRecage({
      path: { studyApiId },
      body: {
        delete_empty_cages: deleteCages,
        cages: proposedCages.map((cage: ProposedCage) => ({
          name: cage.cage,
          animals: cage.animals
            .filter(({ api_id }: RecagingAnimal) => _notNil(api_id))
            .map(({ api_id, newAnimalId, name }: RecagingAnimal) => ({
              animal_api_id: api_id!,
              name: newAnimalId || name || null,
            })),
        })),
      },
    });

    if (response?.type === ResponseType.Success) {
      successToast('Successfully recaged');
      navigate(webRoute('studies.animals', { id: studyId }));
    }
  };

  const handleRecage = () => {
    const { per_cage } = getValues();
    recage(
      selectedSubjects,
      per_cage,
      cageNamingMethod,
      nextCage,
      studyCages,
      existingGroupCageNames,
      setExistingGroupCageNames,
      setProposedCages
    );
  };

  const handleTagChange = (newTags: string[]) => {
    setTags(newTags);
  };

  const changeIdGenerationMethod = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setIdGenerationMethod(event.target.value);
  };

  const changeCageNamingMethod = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCageNamingMethod(event.target.value);
  };

  const generateIds = () => {
    const { sequencePrefix, sequenceStarting } = getValues();
    let update: ProposedCage[] = [];

    if (idGenerationMethod === REPEATING) {
      update = generateRepeatingIds(proposedCages, tags);
    }
    if (idGenerationMethod === SEQUENTIAL) {
      update = generateSequentialIds(proposedCages, sequencePrefix, sequenceStarting);
    }
    setProposedCages(update);
  };

  return (
    <div className="h-100">
      <SubHeader linkToText="Back to study" link={webRoute('studies.show', { id: studyId })} />

      <div className="ph4 pb3">
        <Header mainHeaderText="Recaging" />
        <p className="lh-copy">
          Enter the number of animals to be placed in each new cage and visualize your results below.
          <a
            href={RouteService.help('recaging')}
            target="_blank"
            rel="noopener noreferrer"
            className="dib ml2 link blue"
          >
            Read more.
          </a>
        </p>
        {apiError && <ApiErrorBanner errorType={ApiErrorType.FETCH} error={apiError} />}
        <div className="flex flex-row mt4 mb4">
          <CageGenerationForm
            register={register}
            errors={errors}
            handleSubmit={handleSubmit}
            onSubmit={handleRecage}
            cageNamingMethod={cageNamingMethod}
            onCageNamingMethodChange={changeCageNamingMethod}
            loading={loading}
            studyCagesLoading={studyCagesLoading}
            selectedSubjects={selectedSubjects}
          />
          <IdGenerationForm
            errors={errors}
            idGenerationMethod={idGenerationMethod}
            onIdGenerationMethodChange={changeIdGenerationMethod}
            tags={tags}
            onTagChange={handleTagChange}
            onGenerateIds={generateIds}
            loading={loading}
            studyCagesLoading={studyCagesLoading}
            proposedCages={proposedCages}
            selectedSubjects={selectedSubjects}
            register={register}
          />
        </div>
        {existingGroupCageNames && (
          <Banner info dismiss={false} className="mt3 w-40" testId="randomize-date-banner">
            <h3 className="f6 lh-copy fw5 pb3">
              Cages for these groups already exist. New cages will be assigned the next available alphanumeric label.
            </h3>
          </Banner>
        )}

        {(loading || studyCagesLoading) && <LoadingState />}

        {!loading && !studyCagesLoading && (
          <Fragment>
            {_isEmpty(proposedCages) ? (
              <EmptyState />
            ) : (
              proposedCages.map(({ id, cage, treatmentGroup, animals }, index) => {
                return (
                  <ProposedCageCard
                    key={id}
                    index={index}
                    cage={cage}
                    treatmentGroup={treatmentGroup}
                    animals={animals}
                    onChangeAnimalId={onChangeAnimalId}
                    onChangeCageName={onChangeCageName}
                  />
                );
              })
            )}
          </Fragment>
        )}

        <SubmitSection
          loading={loading}
          studyCagesLoading={studyCagesLoading}
          proposedCages={proposedCages}
          deleteCages={deleteCages}
          onDeleteCagesChange={setDeleteCages}
          onSubmit={onRecageSubmit}
        />
      </div>
      <div className="ph3 ph4-l mb4"></div>
    </div>
  );
};

export default Recaging;
